import React, { useState, useEffect } from 'react';
import { JupasProgram, ScoreBreakdownItem, ScoringFormula } from '../types';
import { translate } from '../lib/utils/translate';
import { INSTITUTIONS_DATA } from '../calculator/data';
import { getEffectiveForbiddenGroups } from '../calculator/calculator';
import { appGetItem, appSetItem } from "@/lib/auth-storage";
import { getUniversityKey } from '../common';
import { SUBJECT_STRAND_MAP } from '../constants';

// Chevron icons as inline SVG components
const ChevronUpIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Get grade values for a programme, considering:
 * 1. Programme-specific grade_values in scoring_formula
 * 2. Institution grade_values from inst_prefix or jupas_id prefix
 * 
 * For SSSDP programmes (inst_prefix = 'S'), extract the first 2 characters
 * of jupas_id (e.g., 'SA' from 'SA01') to look up the specific institution.
 */
function getProgGradeValues(program: JupasProgram, jupas_id: string): Record<string, number> | null {
  // 1. Check for programme-specific grade_values in scoring_formula
  const scoringFormula = program.scoring_formula as ScoringFormula;
  if (scoringFormula?.grade_values) {
    return scoringFormula.grade_values;
  }
  
  // 2. Determine institution key
  let instKey = program.inst_prefix || getUniversityKey(jupas_id);
  
  // For SSSDP programmes (inst_prefix = 'S'), use first 2 chars of jupas_id
  // to get the specific SSSDP institution (e.g., 'SA' for SFU, 'SY' for HKSYU)
  if (instKey === 'S' && jupas_id && jupas_id.length >= 2 && jupas_id.startsWith('S')) {
    const sssdpInstPrefix = jupas_id.substring(0, 2);
    // Check if we have data for this specific SSSDP institution
    if ((INSTITUTIONS_DATA as any)[sssdpInstPrefix]) {
      instKey = sssdpInstPrefix;
    }
  }
  
  const institutionData = instKey ? (INSTITUTIONS_DATA as any)[instKey] : null;
  return institutionData?.grade_values || null;
}

interface ScoringFomulaProps {
  program: JupasProgram;
  matchedSubjects?: ScoreBreakdownItem[];
  userScores?: Record<string, { current: string; min: string; max: string }>;
  selectedScoreType?: 'min' | 'current' | 'max';
}

const HIDDEN_FORMULA_SUBJECTS = new Set(['Hindi']);

const filterVisibleSubjects = (subjects: string[]) => subjects.filter(subject => !HIDDEN_FORMULA_SUBJECTS.has(subject));

export const ScoringFomula: React.FC<ScoringFomulaProps> = ({ program, matchedSubjects = [], userScores, selectedScoreType = 'current' }) => {
  const isMatched = (subjectName: string) => matchedSubjects.some(s => s.subject === subjectName);
  const isRequiredRule = (rule: any) => rule.required === 1 || rule.required === true || rule.mandatory === 1 || rule.mandatory === true;
  const isFlexibleRule = (rule: any) => !isRequiredRule(rule) && (rule.required !== undefined || rule.mandatory !== undefined);
  const getRuleSubjectNames = (rule: any) => filterVisibleSubjects(Object.keys(rule?.subject_weights || {}).filter(subject => subject !== 'any_one_subject'));
  const isInformationalWildcardRule = (rule: any) => {
    const subjectNames = getRuleSubjectNames(rule);
    return !isRequiredRule(rule)
      && Boolean(rule?.subject_weights?.any_one_subject)
      && subjectNames.length === 0
      && Array.isArray(rule?.include_subjects)
      && filterVisibleSubjects(rule.include_subjects).length > 0
      && typeof rule?.note === 'string'
      && rule.note.trim().length > 0
      && !(Array.isArray(rule?.exclude_subjects) && rule.exclude_subjects.length > 0);
  };
  const isWildcardSelectionRule = (rule: any) => {
    const subjectNames = getRuleSubjectNames(rule);
    return !isRequiredRule(rule)
      && Boolean(rule?.subject_weights?.any_one_subject)
      && subjectNames.length === 0
      && !isInformationalWildcardRule(rule);
  };
  const isGroupedRequiredRule = (rule: any) => {
    if (!isRequiredRule(rule)) return false;
    const subjectNames = getRuleSubjectNames(rule);
    const count = typeof rule?.count === 'number' ? rule.count : subjectNames.length;
    return count < subjectNames.length || Boolean(rule?.subject_weights?.any_one_subject);
  };
  const [isGradeTableExpanded, setIsGradeTableExpanded] = React.useState(() => {
    const saved = appGetItem('gradeTableExpanded');
    return saved === 'true';
  });

  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = appGetItem('collapsible_scoringFormula');
      return stored !== null ? stored === 'true' : true;
    }
    return true;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      appSetItem('collapsible_scoringFormula', String(isExpanded));
    }
  }, [isExpanded]);

  const toggleGradeTable = () => {
    setIsGradeTableExpanded(prev => {
      const newValue = !prev;
      appSetItem('gradeTableExpanded', String(newValue));
      return newValue;
    });
  };

  // Get scoring formula (always object format with subject_rules)
  const scoringFormula = program.scoring_formula as ScoringFormula;
  const discrepancyNote = scoringFormula?.discrepancy || (program as any).discrepancy;

  // Get grade_values: check programme-specific first, then SSSDP institution-specific, then general institution
  const gradeValues = getProgGradeValues(program, program.jupas_id);
  
  // Get institution data for forbidden_subject_groups
  const instKey = getUniversityKey(program.jupas_id);
  const institutionData = instKey ? (INSTITUTIONS_DATA as any)[instKey] : null;

  // Function to determine category/subject for a grade
  const getGradeCategory = (grade: string): string => {
    // Category A (HKDSE Core & Electives)
    if (['5**', '5*', '5', '4', '3', '2', '1', 'U'].includes(grade)) {
      return 'Cat A';
    }
    // Category B (Applied Learning)
    if (['Dist II', 'Dist I', 'Att', 'Unatt'].includes(grade)) {
      return 'Cat B';
    }
    // Category C - Urdu (traditional letter grades)
    if (['A++', 'A+', 'A', 'B++', 'B+', 'B', 'C', 'D', 'E'].includes(grade)) {
      return 'Cat C (Urdu)';
    }
    // Category C - French/German/Spanish (CEFR levels)
    if (['C2', 'C1', 'B2', 'B1', 'A2'].includes(grade)) {
      return 'Cat C (French / German / Spanish)';
    }
    // Category C - Japanese
    if (['N1', 'N2', 'N3'].includes(grade)) {
      return 'Cat C (Japanese)';
    }
    // Category C - Korean
    if (['Grade 6', 'Grade 5', 'Grade 4', 'Grade 3'].includes(grade)) {
      return 'Cat C (Korean)';
    }
    // CSD
    if (grade === 'Attained') {
      return 'CSD';
    }
    return 'Other';
  };

  // Sort grade values in a logical order
  const getSortedGradeEntries = (grades: Record<string, any>): [string, any][] => {
    const gradeOrder = [
      // Cat A - HKDSE (high to low)
      '5**', '5*', '5', '4', '3', '2', '1', 'U',
      // Cat B - Applied Learning
      'Dist II', 'Dist I', 'Att', 'Unatt',
      // Cat C - French/German/Spanish CEFR
      'A++', 'A+', 'A', 'B++', 'B+', 'B', 'C', 'D', 'E',
      // Cat C - Chinese
      'C2', 'C1', 'B2', 'B1', 'A2',
      // Cat C - Japanese
      'N1', 'N2', 'N3',
      // Cat C - Korean
      'Grade 6', 'Grade 5', 'Grade 4', 'Grade 3',
      // CSD
      'Attained'
    ];

    return Object.entries(grades).sort((a, b) => {
      const indexA = gradeOrder.indexOf(a[0]);
      const indexB = gradeOrder.indexOf(b[0]);
      
      // If grade not in order list, put it at the end
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      return indexA - indexB;
    });
  };

  return (
    <>
      {/* Scoring Slots */}
      <div className="rounded-lg overflow-hidden bg-blue-50">
        {/* Collapsible Header */}
        <div 
          className="flex items-center justify-between px-3 sm:px-4 py-3 bg-blue-100 cursor-pointer select-none"
          onClick={() => setIsExpanded(prev => !prev)}
        >
          <h3 className="text-xs font-bold text-blue-700 uppercase tracking-wide">
            Scoring Formula
          </h3>
          <button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
            className="p-1 rounded-md hover:bg-blue-200 transition-colors duration-200 text-blue-700"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        </div>

        {/* Collapsible Content */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
        <div className="p-3 sm:p-4">

        {discrepancyNote && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-900">
            <span className="font-semibold">Discrepancy:</span> {discrepancyNote}
          </div>
        )}

        {/* Relevant Category B Subjects (shown when sapl_subjects is defined) */}
        {Array.isArray(program.program_mer?.sapl_subjects) && program.program_mer.sapl_subjects.length > 0 && (
          <div className="mb-4 rounded-md border border-purple-200 bg-purple-50 px-3 py-2">
            <div className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-1">Relevant Category B Subjects</div>
            <div className="flex flex-wrap gap-1">
              {program.program_mer.sapl_subjects.map((subject: string) => (
                <span key={subject} className="inline-block rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-800">{subject}</span>
              ))}
            </div>
          </div>
        )}

        {/* Subject Weights - New simplified format (no mandatory field) */}
        {(() => {
          // Check if we have MER elective requirements (already displayed above)
          const programMer = (program as any).program_mer;
          const hasElectiveMinima = programMer?.elective_minima && Object.keys(programMer.elective_minima).length > 0;
          
          // Count specific elective groups (excluding "ANY 1 SUBJECT" wildcards)
          let numSpecificMerElectives = 0;
          let hasAnySubjectWildcard = false;
          let hasTwoOfRequirement = false;
          if (hasElectiveMinima) {
            Object.entries(programMer.elective_minima).forEach(([key, group]: [string, any]) => {
              const subjectList = Object.keys(group);
              const isWildcard = subjectList.every((s: string) => s.toUpperCase().includes('ANY') && s.toUpperCase().includes('SUBJECT'));
              if (isWildcard) {
                hasAnySubjectWildcard = true;
              } else {
                numSpecificMerElectives++;
              }
              // Check if this is a :two_of requirement
              if (key.includes(':two_of')) {
                hasTwoOfRequirement = true;
              }
            });
          }
          
          // If no subject_rules and there's a :two_of requirement, don't show the "Best X subjects" section
          // because the two_of requirement already covers the elective subjects
          if (!scoringFormula?.subject_rules || scoringFormula.subject_rules.length === 0) {
            // Skip showing "Best X subjects" when there's a two_of requirement
            if (hasTwoOfRequirement) {
              return null;
            }
            
            const numSubjects = scoringFormula?.num_of_subjects || 5;
            const userSubjects = userScores ? Object.keys(userScores) : [];
            const otherSubjectsCount = userSubjects.length;
            
            return (
              <div className="mb-4">
                <div className="text-sm font-semibold text-blue-800 mb-2">
                  Best {numSubjects} subject{numSubjects > 1 ? 's' : ''}:
                </div>
                <ul className="space-y-0 pl-4">
                  <li className="flex items-center justify-between text-sm list-disc ml-4">
                    <span className="text-blue-700">Any eligible subject</span>
                    <span className="text-indigo-600 font-medium flex items-center gap-1">
                      x1
                      <span className="w-5 h-5 flex items-center justify-center">
                        {otherSubjectsCount >= 1 && otherSubjectsCount < numSubjects ? (
                          <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                        ) : otherSubjectsCount >= numSubjects ? (
                          <img src="/images/tick-x2.png" alt="✓✓" className="max-w-none w-[20px] h-[20px]" />
                        ) : null}
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
            );
          }
          
          // New simplified format: rules without required field
          const simplifiedRules = scoringFormula.subject_rules.filter((rule: any) => {
            if (rule.required !== undefined || rule.mandatory !== undefined) {
              return false;
            }

            if (isWildcardSelectionRule(rule) || isInformationalWildcardRule(rule)) {
              return false;
            }

            const subjectWeights = rule.subject_weights || {};
            const specificSubjects = Object.keys(subjectWeights).filter((subject: string) => subject !== 'any_one_subject');
            return specificSubjects.length > 0;
          });
          
          if (simplifiedRules.length === 0) return null;

          // Get subjects from userScores
          const userSubjects = userScores ? Object.keys(userScores) : [];
          
          // Get all subjects from MER elective_minima (these are already shown in "Required Subjects")
          // Exclude "ANY 1 SUBJECT" type wildcards
          const merSubjects = new Set<string>();
          if (hasElectiveMinima) {
            Object.values(programMer.elective_minima).forEach((group: any) => {
              const subjectList = Object.keys(group);
              const isWildcard = subjectList.every((s: string) => s.toUpperCase().includes('ANY') && s.toUpperCase().includes('SUBJECT'));
              if (!isWildcard) {
                subjectList.forEach((subj: string) => merSubjects.add(subj));
              }
            });
          }

          return simplifiedRules.map((rule: any, idx: number) => {
            const count = rule.count || scoringFormula.num_of_subjects || 5;
            const subjectWeights = rule.subject_weights || {};
            const specificSubjects = Object.keys(subjectWeights).filter(s => s !== 'any_one_subject');
            
            // If we have MER elective_minima, don't show weighted subjects again (they're in Required Subjects)
            // Only show subjects that are NOT in MER
            const subjectsToShow = hasElectiveMinima 
              ? specificSubjects.filter(s => !merSubjects.has(s))
              : specificSubjects;
            
            // Helper function to check if user has a subject or any subject belonging to its strand
            const userHasSubjectOrStrand = (targetSubject: string): boolean => {
              // Direct match
              if (userSubjects.includes(targetSubject)) return true;
              // Check if any user subject belongs to this strand
              return userSubjects.some(userSubj => SUBJECT_STRAND_MAP[userSubj] === targetSubject);
            };
            
            // Count how many "other" subjects (not in specificSubjects and not belonging to their strands) are in userScores
            const otherSubjectsCount = userSubjects.filter(s => {
              // Check if subject directly matches any specific subject
              if (specificSubjects.includes(s)) return false;
              // Check if subject's strand matches any specific subject
              if (SUBJECT_STRAND_MAP[s] && specificSubjects.includes(SUBJECT_STRAND_MAP[s])) return false;
              // Also exclude subjects in MER groups
              if (merSubjects.has(s)) return false;
              if (SUBJECT_STRAND_MAP[s] && merSubjects.has(SUBJECT_STRAND_MAP[s])) return false;
              return true;
            }).length;

            // Determine header text based on whether we have MER requirements
            // Only count specific MER electives (not "ANY 1 SUBJECT" wildcards)
            const remainingCount = count - numSpecificMerElectives;
            const headerText = hasElectiveMinima && numSpecificMerElectives > 0
              ? `Other subject${remainingCount > 1 ? 's' : ''} (Best ${remainingCount}):`
              : `Best ${count} subject${count > 1 ? 's' : ''}:`;

            return (
              <div key={`simplified-${idx}`} className="mb-4">
                <div className="text-sm font-semibold text-gray-800 mb-2">
                  {headerText}
                </div>
                <ul className="space-y-0 pl-4">
                  {/* Subjects with specific weights (not already shown in Required Subjects) */}
                  {subjectsToShow.map((subject, subIdx) => {
                    const weight = subjectWeights[subject];
                    const isInUserScores = userHasSubjectOrStrand(subject);
                    return (
                      <li key={`weighted-${subIdx}`} className="flex items-center justify-between text-sm list-disc ml-4">
                        <span className="text-gray-700">{subject}</span>
                        <span className="text-indigo-600 font-medium flex items-center gap-1">
                          x{weight}
                          <span className="w-5 h-5 flex items-center justify-center">
                            {isInUserScores ? (
                              <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                            ) : null}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                  {/* Any other subject with default weight - only show if MER doesn't have "ANY 1 SUBJECT" */}
                  {!hasAnySubjectWildcard && (
                    <li className="flex items-center justify-between text-sm list-disc ml-4">
                      <span className="text-gray-700">Any other subject</span>
                      <span className="text-indigo-600 font-medium flex items-center gap-1">
                        x1
                        <span className="w-5 h-5 flex items-center justify-center">
                          {otherSubjectsCount === 1 ? (
                            <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                          ) : otherSubjectsCount >= 2 ? (
                            <img src="/images/tick-x2.png" alt="✓✓" className="max-w-none w-[20px] h-[20px]" />
                          ) : null}
                        </span>
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            );
          });
        })()}

        {/* Required Subjects - format with required field */}
        {(() => {
          if (!scoringFormula?.subject_rules) return null;
          
          const requiredRules = scoringFormula.subject_rules.filter((rule: any) => isRequiredRule(rule) && !isGroupedRequiredRule(rule));
          if (requiredRules.length === 0) return null;

          return (
            <div className="mb-4">
              <div className="text-sm font-semibold text-indigo-700 mb-2">Required subject(s):</div>
              <ul className="space-y-0 pl-4">
                {requiredRules.map((rule: any, idx: number) => {
                  const subjectNames = getRuleSubjectNames(rule);
                  return subjectNames.map((subjectName, subIdx) => {
                    const weight = rule.subject_weights[subjectName];
                    return (
                      <li key={`required-${idx}-${subIdx}`} className="flex items-center justify-between text-sm list-disc ml-4">
                        <span className="text-indigo-600">
                          {subjectName}
                        </span>
                        <span className="text-indigo-600 font-medium flex items-center gap-1">
                          x{weight}
                          <span className="w-4 h-4 flex items-center justify-center">
                            {isMatched(subjectName) ? (
                              <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                            ) : null}
                          </span>
                        </span>
                      </li>
                    );
                  });
                })}
              </ul>
            </div>
          );
        })()}

        {/* Required grouped subject rules from scoring_formula */}
        {(() => {
          if (!scoringFormula?.subject_rules) return null;

          const merSpecificGroups = Object.values((program as any).program_mer?.elective_minima || {}).map((group: any) =>
            Object.keys(group as Record<string, number>)
              .filter(subject => !(subject.toUpperCase().includes('ANY') && subject.toUpperCase().includes('SUBJECT')))
              .sort()
          ).filter((subjects: string[]) => subjects.length > 0);

          const groupedRequiredRules = scoringFormula.subject_rules.filter((rule: any) => {
            if (!isGroupedRequiredRule(rule)) {
              return false;
            }

            const subjectNames = getRuleSubjectNames(rule).sort();
            return !merSpecificGroups.some(group =>
              group.length === subjectNames.length && group.every((subject, index) => subject === subjectNames[index])
            );
          });
          if (groupedRequiredRules.length === 0) return null;

          return groupedRequiredRules.map((rule: any, idx: number) => {
            const subjectNames = getRuleSubjectNames(rule);
            const count = typeof rule.count === 'number' ? rule.count : subjectNames.length;
            const heading = count === 1
              ? 'Required one of the following subjects:'
              : count === 2
                ? 'Required two of the following subjects:'
                : `Required ${count} of the following subjects:`;

            return (
              <div key={`grouped-required-${idx}`} className="mb-4">
                <div className="text-sm font-semibold text-indigo-700 mb-2">{heading}</div>
                <ul className="space-y-0 pl-4">
                  {subjectNames.map((subjectName, subIdx) => {
                    const weight = rule.subject_weights[subjectName];
                    return (
                      <li key={`grouped-required-item-${idx}-${subIdx}`} className="flex items-center justify-between text-sm list-disc ml-4">
                        <span className="text-indigo-600">{subjectName}</span>
                        <span className="text-indigo-600 font-medium flex items-center gap-1">
                          x{weight}
                          <span className="w-4 h-4 flex items-center justify-center">
                            {isMatched(subjectName) ? (
                              <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                            ) : null}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          });
        })()}

        {/* MER Elective Requirements - show required elective groups from program_mer */}
        {(() => {
          const programMer = (program as any).program_mer;
          if (!programMer?.elective_minima) return null;

          const electiveMinima = programMer.elective_minima;
          const electiveGroups = Object.entries(electiveMinima);
          if (electiveGroups.length === 0) return null;

          const specificElectiveGroups = electiveGroups.filter(([_, subjects]) => {
            const subjectList = Object.keys(subjects as Record<string, number>);
            return !subjectList.every(s => s.toUpperCase().includes('ANY') && s.toUpperCase().includes('SUBJECT'));
          });
          if (specificElectiveGroups.length === 0) return null;

          const userSubjects = userScores ? Object.keys(userScores) : [];
          const subjectRules = scoringFormula?.subject_rules || [];

          const userHasSubjectOrStrand = (targetSubject: string): boolean => {
            if (userSubjects.includes(targetSubject)) return true;
            return userSubjects.some(userSubj => SUBJECT_STRAND_MAP[userSubj] === targetSubject);
          };

          const getSubjectWeight = (subject: string): number => {
            for (const rule of subjectRules) {
              const ruleWeights = rule.subject_weights || {};
              if (subject.toUpperCase().includes('ANY') && subject.toUpperCase().includes('SUBJECT') && ruleWeights.any_one_subject !== undefined) {
                return ruleWeights.any_one_subject;
              }
              if (ruleWeights[subject] !== undefined) return ruleWeights[subject];
              const strand = SUBJECT_STRAND_MAP[subject];
              if (strand && ruleWeights[strand] !== undefined) return ruleWeights[strand];
            }
            return 1;
          };

          return specificElectiveGroups.map(([key, subjects]) => {
            const subjectList = Object.keys(subjects as Record<string, number>);
            const isTwoOf = key.includes(':two_of');
            const heading = isTwoOf ? 'Required two of the following subjects:' : 'Required one of the following subjects:';

            return (
              <div key={key} className="mb-4">
                <div className="text-sm font-semibold text-indigo-700 mb-2">{heading}</div>
                <ul className="space-y-0 pl-4">
                  {subjectList.map((subject, subIdx) => {
                    const weight = getSubjectWeight(subject);
                    const isInUserScores = userHasSubjectOrStrand(subject);
                    let displaySubject = subject;
                    if (subject.toUpperCase().includes('ANY') && subject.toUpperCase().includes('SUBJECT')) {
                      const match = subject.match(/(\d+)\s+SUBJECT/i);
                      if (match) {
                        const count = parseInt(match[1]);
                        displaySubject = count === 1 ? 'Any one subject' : `Any ${count} subjects`;
                      } else {
                        displaySubject = 'Any one subject';
                      }
                    }
                    return (
                      <li key={`${key}-${subIdx}`} className="flex items-center justify-between text-sm list-disc ml-4">
                        <span className="text-indigo-600">{displaySubject}</span>
                        <span className="text-indigo-600 font-medium flex items-center gap-1">
                          x{weight}
                          <span className="w-5 h-5 flex items-center justify-center">
                            {isInUserScores ? (
                              <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                            ) : null}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          });
        })()}

        {/* Best N Subjects with Specific Weights (flexible rules) */}
        {(() => {
          if (!scoringFormula?.subject_rules) return null;

          const programMer = (program as any).program_mer;
          const merElectiveMinima = programMer?.elective_minima || {};
          const merSpecificSubjects = new Set<string>();
          let hasMerAnySubjectWildcard = false;
          let hasMerTwoOfGroup = false;

          Object.entries(merElectiveMinima).forEach(([key, group]) => {
            const subjects = Object.keys(group as Record<string, number>);
            if (key.includes(':two_of')) {
              hasMerTwoOfGroup = subjects.some(subject => !(subject.toUpperCase().includes('ANY') && subject.toUpperCase().includes('SUBJECT')));
            }
            subjects.forEach(subject => {
              if (subject.toUpperCase().includes('ANY') && subject.toUpperCase().includes('SUBJECT')) {
                hasMerAnySubjectWildcard = true;
              } else {
                merSpecificSubjects.add(subject);
              }
            });
          });

          const informationalWildcardRules = scoringFormula.subject_rules.filter((rule: any) => isInformationalWildcardRule(rule));

          const flexibleRules = scoringFormula.subject_rules.filter((rule: any) => isFlexibleRule(rule) || isWildcardSelectionRule(rule)).filter((rule: any) => {
            if (isInformationalWildcardRule(rule)) {
              return false;
            }

            const subjectWeights = rule.subject_weights || {};
            const specificSubjects = Object.keys(subjectWeights).filter((subject: string) => subject !== 'any_one_subject');
            const hasWildcard = 'any_one_subject' in subjectWeights;
            const excludedSubjects = Array.isArray(rule.exclude_subjects) ? rule.exclude_subjects : [];
            const includedSubjects = Array.isArray(rule.include_subjects) ? filterVisibleSubjects(rule.include_subjects) : [];
            const hasSpecialWildcardConstraints = excludedSubjects.length > 0
              || includedSubjects.length > 0
              || (typeof rule.note === 'string' && rule.note.trim().length > 0);

            if (hasMerTwoOfGroup && specificSubjects.length > 0 && specificSubjects.every((subject: string) => merSpecificSubjects.has(subject))) {
              return false;
            }
            if (hasMerAnySubjectWildcard && specificSubjects.length === 0 && hasWildcard && !hasSpecialWildcardConstraints) {
              return false;
            }
            return true;
          });
          if (flexibleRules.length === 0) return null;

          return flexibleRules.map((rule: any, idx: number) => {
            const count = rule.count;
            const subjectWeights = rule.subject_weights || {};
            const hasWildcard = 'any_one_subject' in subjectWeights;
            const specificSubjects = Object.keys(subjectWeights).filter(s => s !== 'any_one_subject');
            const wildcardWeight = hasWildcard ? subjectWeights['any_one_subject'] : 1;
            const excludedSubjects = Array.isArray(rule.exclude_subjects) ? rule.exclude_subjects : [];
            const wildcardOnlyRule = hasWildcard && specificSubjects.length === 0;
            const attachedInformationalRule = informationalWildcardRules.find((infoRule: any) => {
              const infoIncludedSubjects = Array.isArray(infoRule.include_subjects) ? filterVisibleSubjects(infoRule.include_subjects) : [];
              return excludedSubjects.length > 0 && infoIncludedSubjects.some((subject: string) => excludedSubjects.includes(subject));
            });

            // Count how many "other eligible subjects" are matched (not in specificSubjects)
            const otherMatchedCount = matchedSubjects.filter(s => 
              !specificSubjects.includes(s.subject) && 
              !excludedSubjects.includes(s.subject) &&
              s.slotType !== 'CORE' && 
              s.mandatory !== true
            ).length;

            const displayRequiredCount = wildcardOnlyRule
              ? count + (attachedInformationalRule?.count || 0)
              : count;
            const sectionHeading = wildcardOnlyRule
              ? `Required ${displayRequiredCount} more subject${displayRequiredCount > 1 ? 's' : ''}:`
              : `Best ${count} other subject${count > 1 ? 's' : ''}:`;
            const wildcardLabel = wildcardOnlyRule
              ? (attachedInformationalRule
                  ? 'Any other subject'
                  : excludedSubjects.length > 0
                  ? `Any other subject (except ${excludedSubjects.map((subject: string) => subject.includes('Module 1') ? 'M1' : subject.includes('Module 2') ? 'M2' : subject).join(' and ')})`
                  : 'Any other subject')
              : 'Other eligible subjects';

            return (
              <div key={`flexible-${idx}`} className="mb-4">
                <div className="text-sm font-semibold text-gray-800 mb-2">
                  {sectionHeading}
                </div>
                <ul className="space-y-0 pl-4">
                  {/* {specificSubjects.length > 0 && (
                    <div className="text-xs text-gray-600 mb-1">Subjects with specific weights:</div>
                  )} */}
                  {specificSubjects.map((subject, subIdx) => {
                    const weight = subjectWeights[subject];
                    return (
                      <li key={`specific-${subIdx}`} className="flex items-center justify-between text-sm list-disc ml-4">
                        <span className="text-gray-700">{subject}</span>
                        <span className="text-indigo-600 font-medium flex items-center gap-1">
                          x{weight}
                          <span className="w-4 h-4 flex items-center justify-center">
                            {isMatched(subject) ? (
                              <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                            ) : null}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                  {hasWildcard && (
                    <>
                    <li className="flex items-center justify-between text-sm list-disc ml-4">
                      <span className="text-gray-700">{wildcardLabel}</span>
                      <span className="text-indigo-600 font-medium flex items-center gap-1">
                        x{wildcardWeight}
                        <span className="w-4 h-4 flex items-center justify-center">
                          {otherMatchedCount === 1 ? (
                            <img src="/images/tick-x1.png" alt="✓" className="max-w-none w-[20px] h-[20px]" />
                          ) : otherMatchedCount >= 2 ? (
                            <img src="/images/tick-x2.png" alt="✓✓" className="max-w-none w-[20px] h-[20px]" />
                          ) : null}
                        </span>
                      </span>
                    </li>
                    {(rule.note || attachedInformationalRule?.note) && (
                      <li className="text-xs text-slate-700 ml-4 list-none leading-relaxed mt-2 border-t border-slate-300 bg-slate-200 px-3 py-2 rounded-sm">
                        Note: {rule.note || attachedInformationalRule?.note}
                      </li>
                    )}
                    </>
                  )}
                </ul>
              </div>
            );
          });
        })()}


        {/* Grade to Score Conversion Table */}
        {gradeValues && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-800">Grade to Score Conversion:</div>
              <button
                onClick={toggleGradeTable}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                aria-label={isGradeTableExpanded ? "Collapse table" : "Expand table"}
              >
                {isGradeTableExpanded ? 'Hide details' : 'Show details'}
                <svg 
                  className={`w-4 h-4 transition-transform duration-300 ${isGradeTableExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            <div 
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: isGradeTableExpanded ? '1000px' : '500px',
                opacity: 1
              }}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-2 py-1 text-left font-semibold text-gray-700">Category/Subject</th>
                      <th className="border border-gray-300 px-2 py-1 text-center font-semibold text-gray-700" style={{width: '80px'}}>Grade</th>
                      <th className="border border-gray-300 px-2 py-1 text-center font-semibold text-gray-700" style={{width: '80px'}}>Score</th>
                    </tr>
                  </thead>
                  <tbody className="transition-opacity duration-200">
                    {(() => {
                      const sortedEntries = getSortedGradeEntries(gradeValues);
                      
                      // Get user's grades for filtering when collapsed
                      const userGrades = userScores ? Object.values(userScores).map(scoreTriple => scoreTriple[selectedScoreType]) : [];
                      
                      // Filter entries based on expanded state
                      const filteredEntries = isGradeTableExpanded 
                        ? sortedEntries 
                        : sortedEntries.filter(([grade]) => userGrades.includes(grade));
                      
                      const categoryStartIndices: Record<string, number> = {};
                      const categoryCounts: Record<string, number> = {};
                      
                      // First pass: count rows per category (using filtered entries)
                      filteredEntries.forEach(([grade], idx) => {
                        const category = getGradeCategory(grade);
                        if (!categoryCounts[category]) {
                          categoryCounts[category] = 0;
                          categoryStartIndices[category] = idx;
                        }
                        categoryCounts[category]++;
                      });
                      
                      // Second pass: check which categories have highlighted grades
                      const highlightedCategories = new Set<string>();
                      if (userScores) {
                        filteredEntries.forEach(([grade]) => {
                          if (userGrades.includes(grade)) {
                            highlightedCategories.add(getGradeCategory(grade));
                          }
                        });
                      }
                      
                      return filteredEntries.map(([grade, score], idx) => {
                        const category = getGradeCategory(grade);
                        const isFirstInCategory = categoryStartIndices[category] === idx;
                        const rowspan = categoryCounts[category];
                        
                        // Check if this grade is being used by the user (current score type only)
                        const isUsedGrade = userScores ? Object.values(userScores).some(
                          scoreTriple => scoreTriple[selectedScoreType] === grade
                        ) : false;
                        
                        // Check if this category should be highlighted
                        const isCategoryHighlighted = highlightedCategories.has(category);
                        
                        return (
                          <tr 
                            key={`${grade}-${idx}`} 
                            className={`
                              transition-all duration-200 ease-in-out
                              ${isUsedGrade ? 'bg-green-50' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
                              animate-fadeIn
                            `}
                            style={{
                              animation: 'fadeIn 0.3s ease-in-out'
                            }}
                          >
                            {isFirstInCategory && (
                              <td 
                                className={`border border-gray-300 px-2 py-1 text-gray-600 text-xs align-top ${isCategoryHighlighted ? 'bg-green-50' : ''}`}
                                rowSpan={rowspan}
                              >
                                {category}
                              </td>
                            )}
                            <td className="border border-gray-300 px-2 py-1 text-center text-gray-800">{grade}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center text-gray-600">{String(score)}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}


        {/* Forbidden Subject Groups */}
        {(() => {
          const forbiddenGroups = getEffectiveForbiddenGroups(program, institutionData);
          if (!forbiddenGroups || forbiddenGroups.length === 0) return null;

          // Get list of ALL subjects the user has selected (from userScores, not just matchedSubjects)
          // This ensures we detect conflicts even for subjects that were filtered out from score calculation
          const userSelectedSubjects = new Set(
            userScores ? Object.keys(userScores).filter(subj => {
              const scores = userScores[subj];
              // Only include subjects where user has entered at least one grade
              return scores && (scores.current || scores.min || scores.max);
            }) : []
          );

          return (
            <div className="mb-4">
              <div className="text-sm font-semibold text-gray-800 mb-2">
                Forbidden Subject Groups:
              </div>
              <ul className="space-y-0 pl-4">
                {forbiddenGroups.map((group: string[], idx: number) => {
                  // Check if user has selected multiple subjects from this group (actual conflict)
                  const selectedFromGroup = group.filter(subject => userSelectedSubjects.has(subject));
                  const hasForbiddenCombination = selectedFromGroup.length >= 2;
                  
                  return (
                    <li 
                      key={idx}
                      className={`text-sm py-1 px-3 rounded list-disc ml-4 ${
                        hasForbiddenCombination 
                          ? 'bg-amber-100 border border-amber-400' 
                          : ''
                      }`}
                    >
                      {hasForbiddenCombination && (
                        <span className="font-bold mr-2 text-amber-600">⚠</span>
                      )}
                      {group.map((subject: string, subIdx: number) => (
                        <React.Fragment key={subIdx}>
                          <span className={`${hasForbiddenCombination && userSelectedSubjects.has(subject) ? 'font-semibold text-amber-800' : ''}`}>
                            {subject}
                          </span>
                          {subIdx < group.length - 1 && (
                            <span className={`font-semibold ${hasForbiddenCombination ? 'text-amber-500' : 'text-gray-600'}`}>
                              ;{' '}
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })()}
        </div>
        </div>
      </div>
    </>
  );
};
